// src/components/dashboard/miDieta/DietSection.js
/*  ──────────────────────────  IMPORTS  ────────────────────────── */
import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, Divider, Stack, Typography } from "@mui/material";
import {
  BreakfastDining as BreakfastDiningIcon,
  DinnerDining as DinnerDiningIcon,
  Fastfood as FastfoodIcon,
  LunchDining as LunchDiningIcon,
  LocalDrink as LocalDrinkIcon,
  LocalGroceryStore as LocalGroceryStoreIcon,
  PieChart as PieChartIcon,
  Refresh as RefreshIcon,
  ShoppingCart as ShoppingCartIcon,
  LightbulbOutlined as LightbulbIcon,
} from "@mui/icons-material";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import DailyDietTabs from "./DailyDietTabs";
import DietChart from "./DietChart";
import {
  getGrupoByCategoriaId,
  normalizeIngredientForSuper,
} from "../../../utils/Utils";
import IngredientesService from "../../../services/IngredientesService";

/*  ──────────────────────────  CONSTANTES  ────────────────────────── */
const mealIcons = {
  desayuno: <BreakfastDiningIcon />,
  comida: <LunchDiningIcon />,
  cena: <DinnerDiningIcon />,
  colacion1: <FastfoodIcon />,
  colacion2: <FastfoodIcon />,
  colacion3: <FastfoodIcon />,
};
const daysOrder = [
  "lunes",
  "martes",
  "miercoles",
  "jueves",
  "viernes",
  "sabado",
  "domingo",
];
const mealsOrder = [
  "desayuno",
  "comida",
  "cena",
  "colacion1",
  "colacion2",
  "colacion3",
];
const capitalize = (s = "") => s.charAt(0).toUpperCase() + s.slice(1);
const GROUP_PRIORITY = [
  "Frutas y verduras",
  "Alimentos de origen animal",
  "Lacteos",
];
const toArray = (v) => (Array.isArray(v) ? v : v ? Object.values(v) : []);

/*  ──────────────────  LISTA DE SÚPER (agrupación y formato)  ────────────────── */
const canonical = (qty = 0, unit = "") => {
  if (unit === "litros") return { qty: qty * 1000, unit: "ml" };
  if (unit === "kilos") return { qty: qty * 1000, unit: "gramos" };
  return { qty, unit };
};

const makeDisplay = (qty = 0, unit = "") => {
  if (unit === "gramos") {
    return qty >= 1000
      ? `${(qty / 1000).toFixed(2)} kilos (${qty.toFixed(2)} g)`
      : `${qty.toFixed(2)} g`;
  }
  if (unit === "ml") {
    return qty >= 1000
      ? `${(qty / 1000).toFixed(2)} litros (${qty.toFixed(2)} ml)`
      : `${qty.toFixed(2)} ml`;
  }
  if (unit === "pza") return `${qty.toFixed(2)} piezas`;
  return `${qty.toFixed(2)} ${unit}`;
};

function buildSupermarketGroups(dietas) {
  if (!dietas?.length) return {};
  const dieta = dietas[dietas.length - 1];
  const days = dieta.dias?.[0] || {};

  const map = new Map();
  const add = ({ group, name, unit, qty }) => {
    const { qty: canonQty, unit: canonUnit } = canonical(qty, unit);
    const key = `${group}||${name}||${canonUnit}`;
    map.set(
      key,
      map.has(key)
        ? { ...map.get(key), qty: map.get(key).qty + canonQty }
        : { group, name, unit: canonUnit, qty: canonQty }
    );
  };

  const handleIngredient = (ing) => {
    const normalized = normalizeIngredientForSuper(ing);
    const catId = ing.categoria?._id || ing.categoria || "";
    const fallback = ing.categoria?.nombre || ing.categoriaNombre || "Otros";
    const group = getGrupoByCategoriaId(catId, fallback);
    add({ group, ...normalized });
  };

  Object.values(days).forEach((dia) =>
    (dia.alimentosDia || []).forEach((momento) =>
      mealsOrder.forEach((meal) =>
        (momento[meal]?.contenido || []).forEach((item) => {
          if (item.ingrediente) handleIngredient(item.ingrediente);
          if (item.platillo)
            toArray(item.platillo.ingredientes).forEach(handleIngredient);
          if (item.comida) {
            toArray(item.comida.ingredientes).forEach(handleIngredient);
            toArray(item.comida.platillos).forEach((p) =>
              toArray(p.ingredientes).forEach(handleIngredient)
            );
          }
        })
      )
    )
  );

  const grouped = {};
  map.forEach((value) => {
    const display = makeDisplay(value.qty, value.unit);
    if (!grouped[value.group]) grouped[value.group] = [];
    grouped[value.group].push({ name: value.name, display });
  });

  Object.keys(grouped).forEach((group) =>
    grouped[group].sort((a, b) => a.name.localeCompare(b.name))
  );

  return grouped;
}

function aggregateSupplements(dietas) {
  if (!dietas?.length) return [];
  const dieta = dietas[dietas.length - 1];
  const days = dieta.dias?.[0] || {};
  const acc = {};

  Object.values(days).forEach((dia) =>
    (dia.alimentosDia || []).forEach((momento) =>
      mealsOrder.forEach((meal) =>
        (momento[meal]?.contenido || []).forEach((item) => {
          if (item.suplemento) {
            const s = item.suplemento;
            const key = s.nombre || "Suplemento";
            acc[key] = (acc[key] || 0) + Number(s.pesoNeto || s.cantidad || 0);
          }
        })
      )
    )
  );

  return Object.entries(acc)
    .map(([name, net]) => ({ name, net: Number(net.toFixed(2)) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

const DietSection = ({
  dietasAsignadas = [],
  loading,
  error,
  fetchDietasAsignadas,
  selectedDay,
  setSelectedDay,
  color3,
  nutriologoNombre,
  direccionAMostrar,
  telefonoAMostrar,
}) => {
  const [ingredientes, setIngredientes] = useState([]);

  useEffect(() => {
    let mounted = true;
    IngredientesService.fetchIngredientes()
      .then((data) => {
        if (mounted) setIngredientes(Array.isArray(data) ? data : []);
      })
      .catch(() => mounted && setIngredientes([]));
    return () => {
      mounted = false;
    };
  }, []);

  const accentColor = color3 || "#2563eb";
  const secondaryAccent = "#0f172a";
  const glassCardStyles = useMemo(
    () => ({
      borderRadius: 4,
      background: "linear-gradient(180deg,#ffffff,#f8fafc)",
      p: { xs: 2, md: 3 },
      boxShadow: "0 22px 55px rgba(15,23,42,0.12)",
      border: "1px solid rgba(148,163,184,0.24)",
    }),
    []
  );

  const supermarketGroups = useMemo(
    () => buildSupermarketGroups(dietasAsignadas),
    [dietasAsignadas]
  );
  const supplements = useMemo(
    () => aggregateSupplements(dietasAsignadas),
    [dietasAsignadas]
  );
  const orderedGroups = useMemo(() => {
    const prioritized = GROUP_PRIORITY.filter(
      (group) => supermarketGroups[group]?.length
    );
    const rest = Object.keys(supermarketGroups)
      .filter((group) => !GROUP_PRIORITY.includes(group))
      .sort();
    return [...prioritized, ...rest];
  }, [supermarketGroups]);

  const lastDiet = dietasAsignadas[dietasAsignadas.length - 1];
  const availableDays = useMemo(
    () => Object.keys(lastDiet?.dias?.[0] || {}),
    [lastDiet]
  );

  const dietRecommendations = useMemo(() => {
    const raw = lastDiet?.recomendaciones;
    if (!raw) return [];
    const entries = Array.isArray(raw) ? raw : Object.values(raw || {});
    return entries
      .map((rec, idx) => {
        if (!rec) return null;
        if (typeof rec === "string") {
          return {
            id: String(idx),
            titulo: `Recomendación ${idx + 1}`,
            descripcion: rec,
            todasLasDietas: false,
          };
        }
        const titulo = rec.titulo || rec.title || `Recomendación ${idx + 1}`;
        const descripcion =
          rec.descripcion || rec.description || rec.texto || rec.body || "";
        return {
          id: rec._id || rec.id || String(idx),
          titulo,
          descripcion,
          todasLasDietas: Boolean(rec.todasLasDietas),
        };
      })
      .filter(Boolean);
  }, [lastDiet]);
  const hasDietRecommendations = dietRecommendations.length > 0;
  useEffect(() => {
    if (!availableDays.length || !setSelectedDay) return;
    if (!availableDays.includes(selectedDay)) {
      setSelectedDay(availableDays[0]);
    }
  }, [availableDays, selectedDay, setSelectedDay]);

  const handleGenerateSuperPDF = () => {
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    doc.setFontSize(18);
    doc.text("Lista de super", 40, 50);

    let cursorY = 80;
    if (supplements.length) {
      doc.setFontSize(14);
      doc.text("Suplementos", 40, cursorY);
      cursorY += 12;
      autoTable(doc, {
        startY: cursorY,
        head: [["Nombre", "Cantidad"]],
        body: supplements.map((s) => [s.name, `${s.net.toFixed(2)} g`]),
        headStyles: { fillColor: [37, 99, 235] },
      });
      cursorY = doc.lastAutoTable.finalY + 20;
    }

    orderedGroups.forEach((group) => {
      const items = supermarketGroups[group];
      if (!items?.length) return;
      doc.setFontSize(14);
      doc.text(group, 40, cursorY);
      cursorY += 12;
      autoTable(doc, {
        startY: cursorY,
        head: [["Ingrediente", "Cantidad"]],
        body: items.map((it) => [it.name, it.display]),
        headStyles: { fillColor: [37, 99, 235] },
      });
      cursorY = doc.lastAutoTable.finalY + 18;
    });

    const blobUrl = URL.createObjectURL(doc.output("blob"));
    window.open(blobUrl, "_blank");
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: 320,
          display: "grid",
          placeItems: "center",
          background: "linear-gradient(135deg,#e0f2fe,#f8fafc)",
          borderRadius: 4,
          boxShadow: "0 18px 45px rgba(15,23,42,0.15)",
          px: 3,
        }}
      >
        <Stack alignItems="center" spacing={1.5}>
          <RefreshIcon
            sx={{
              fontSize: 42,
              color: accentColor,
              animation: "spin 1.8s linear infinite",
            }}
          />
          <Typography fontWeight={600} color="text.primary">
            Cargando tu plan personalizado...
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          maxWidth: 520,
          mx: "auto",
          background: "linear-gradient(160deg,#fee2e2,rgba(255,255,255,0.95))",
          borderRadius: 4,
          p: { xs: 3, md: 4 },
          boxShadow: "0 22px 50px rgba(248,113,113,0.25)",
        }}
      >
        <Stack spacing={2} alignItems="flex-start">
          <Typography variant="h5" fontWeight={800} color="#b91c1c">
            Algo no salió bien
          </Typography>
          <Typography color="text.secondary">{error}</Typography>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={fetchDietasAsignadas}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.2,
              fontWeight: 700,
              background: accentColor,
              boxShadow: "0 12px 25px rgba(37,99,235,0.35)",
            }}
          >
            Reintentar
          </Button>
        </Stack>
      </Box>
    );
  }

  if (!dietasAsignadas.length) {
    return (
      <Box
        sx={{
          background: "#fff",
          borderRadius: 4,
          p: { xs: 3, md: 4 },
          boxShadow: "0 20px 45px rgba(15,23,42,0.08)",
        }}
      >
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: 3,
                background: `${accentColor}16`,
                display: "grid",
                placeItems: "center",
                color: accentColor,
              }}
            >
              <LocalGroceryStoreIcon />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={800} color="text.primary">
                Sin dieta asignada
              </Typography>
              <Typography color="text.secondary">
                {`Contacta a ${nutriologoNombre || "tu nutriólogo"}`}
              </Typography>
            </Box>
          </Stack>
          {direccionAMostrar && (
            <Typography color="text.secondary">
              📍 {direccionAMostrar}
            </Typography>
          )}
          {telefonoAMostrar && (
            <Typography color="text.secondary">
              📞 {telefonoAMostrar}
            </Typography>
          )}
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        background: "linear-gradient(180deg,#f1f5f9 0%,#f8fafc 100%)",
        py: { xs: 3, md: 5 },
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 1200,
          mx: "auto",
        }}
      >
        <Stack spacing={{ xs: 2.5, md: 3.5 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(2, minmax(0, 1fr))",
                lg: "minmax(0, 2fr) minmax(0, 1fr)",
              },
              columnGap: { xs: 0, md: 3 },
              rowGap: { xs: 2.5, md: 3 },
              alignItems: "stretch",
            }}
          >
            <Box sx={{ height: "100%", minWidth: 0 }}>
              <Box sx={{ ...glassCardStyles, height: "100%" }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                  mb={2}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        background: `${accentColor}15`,
                        display: "grid",
                        placeItems: "center",
                        color: accentColor,
                      }}
                    >
                      {mealIcons[selectedDay] || <FastfoodIcon />}
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight={800}>
                        Vista rápida del día
                      </Typography>
                      <Typography color="text.secondary" fontSize={14}>
                        {`Plan para ${capitalize(selectedDay)}`}
                      </Typography>
                    </Box>
                  </Stack>
                  <Chip
                    label={capitalize(selectedDay)}
                    sx={{
                      borderRadius: 2,
                      background: `${accentColor}12`,
                      color: accentColor,
                      fontWeight: 700,
                    }}
                  />
                </Stack>
                <DailyDietTabs
                  dietasAsignadas={[lastDiet]}
                  selectedDay={selectedDay}
                  setSelectedDay={setSelectedDay}
                  color3={color3}
                  ingredientes={ingredientes}
                />
              </Box>
            </Box>

            <Stack spacing={2.5} sx={{ minWidth: 0, height: "100%" }}>
              <Box sx={glassCardStyles}>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  mb={1.5}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      background: `${accentColor}15`,
                      display: "grid",
                      placeItems: "center",
                      color: accentColor,
                    }}
                  >
                    <PieChartIcon />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={800}>
                      Distribución nutricional
                    </Typography>
                    <Typography color="text.secondary" fontSize={13}>
                      Calorías, macros y balance
                    </Typography>
                  </Box>
                </Stack>
                <DietChart
                  dietasAsignadas={[lastDiet]}
                  selectedDay={selectedDay}
                  accentColor={accentColor}
                />
              </Box>

              <Box sx={{ ...glassCardStyles, p: { xs: 2.5, md: 3 } }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                  mb={1.5}
                >
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        background: `${accentColor}15`,
                        display: "grid",
                        placeItems: "center",
                        color: accentColor,
                      }}
                    >
                      <LocalGroceryStoreIcon />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={800}>
                        Lista de super
                      </Typography>
                      <Typography color="text.secondary" fontSize={13}>
                        Ingredientes
                      </Typography>
                    </Box>
                  </Stack>
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<ShoppingCartIcon />}
                    onClick={handleGenerateSuperPDF}
                    sx={{ fontWeight: 700, color: accentColor }}
                  >
                    PDF
                  </Button>
                </Stack>

                {supplements.length > 0 && (
                  <Box
                    sx={{
                      mb: 2,
                      borderRadius: 2,
                      background: "rgba(16,185,129,0.1)",
                      p: 1.75,
                    }}
                  >
                    <Typography fontWeight={700} color="#047857" mb={1}>
                      Suplementos
                    </Typography>
                    <Stack spacing={1}>
                      {supplements.map((s) => (
                        <Stack
                          key={s.name}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ fontSize: 14 }}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <LocalDrinkIcon
                              sx={{ color: "#0f766e", fontSize: 18 }}
                            />
                            <Typography fontWeight={600}>{s.name}</Typography>
                          </Stack>
                          <Typography fontWeight={700} color="text.secondary">
                            {`${s.net.toFixed(2)} g`}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                )}

                <Divider sx={{ mb: 2 }} />

                <Stack
                  spacing={1.2}
                  sx={{ maxHeight: 360, overflowY: "auto", pr: 1 }}
                >
                  {orderedGroups.map((group) => {
                    const items = supermarketGroups[group];
                    if (!items?.length) return null;
                    return (
                      <Box key={group}>
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          mb={1}
                          color={secondaryAccent}
                        >
                          {group}
                        </Typography>
                        <Stack spacing={0.4}>
                          {items.map((it, idx) => (
                            <Stack
                              key={`${group}-${it.name}-${idx}`}
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              sx={{
                                borderRadius: 2,
                                border: "1px solid rgba(15,23,42,0.08)",
                                px: 0.9,
                                py: 0.45,
                                background:
                                  "linear-gradient(120deg,#f8fafc,#fff)",
                              }}
                            >
                              <Stack
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                              >
                                <FastfoodIcon
                                  sx={{ color: accentColor, fontSize: 12 }}
                                />
                                <Typography
                                  fontWeight={600}
                                  sx={{ fontSize: 12 }}
                                >
                                  {it.name}
                                </Typography>
                              </Stack>
                              <Typography
                                fontWeight={600}
                                color="text.secondary"
                                sx={{ fontSize: 12 }}
                              >
                                {it.display}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            </Stack>
          </Box>

          <Box sx={glassCardStyles}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              mb={2}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    background: `${accentColor}15`,
                    display: "grid",
                    placeItems: "center",
                    color: accentColor,
                  }}
                >
                  <LightbulbIcon />
                </Box>
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight={800}
                    color={secondaryAccent}
                  >
                    Recomendaciones de tu plan
                  </Typography>
                  <Typography color="text.secondary" fontSize={13}>
                    Consejos añadidos por tu nutriólogo
                  </Typography>
                </Box>
              </Stack>
              {hasDietRecommendations && (
                <Chip
                  label={`${dietRecommendations.length} ${
                    dietRecommendations.length === 1
                      ? "recomendación"
                      : "recomendaciones"
                  }`}
                  sx={{
                    fontWeight: 600,
                    background: `${accentColor}12`,
                    color: accentColor,
                  }}
                />
              )}
            </Stack>

            {hasDietRecommendations ? (
              <Stack spacing={1.5}>
                {dietRecommendations.map((rec) => (
                  <Box
                    key={rec.id}
                    sx={{
                      borderRadius: 3,
                      border: "1px solid rgba(15,23,42,0.08)",
                      background: "linear-gradient(135deg,#f8fafc,#fff)",
                      p: 2,
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      justifyContent="space-between"
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        color={secondaryAccent}
                      >
                        {rec.titulo}
                      </Typography>
                      {rec.todasLasDietas && (
                        <Chip
                          label="Aplica a todas tus dietas"
                          size="small"
                          sx={{
                            background: `${accentColor}15`,
                            color: accentColor,
                            fontWeight: 600,
                          }}
                        />
                      )}
                    </Stack>
                    {rec.descripcion && (
                      <Typography
                        color="text.secondary"
                        fontSize={14}
                        mt={0.75}
                      >
                        {rec.descripcion}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            ) : (
              <Box
                sx={{
                  py: 4,
                  px: 2,
                  textAlign: "center",
                  borderRadius: 3,
                  border: "1px dashed rgba(15,23,42,0.15)",
                  background: "linear-gradient(135deg,#f8fafc,#fff)",
                }}
              >
                <Typography color="text.secondary">
                  Tu nutriólogo aún no agrega recomendaciones para esta dieta.
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default DietSection;
